import { Controller } from '@hotwired/stimulus';
import { visit } from '@hotwired/turbo';

export default class extends Controller {
  static values = {
    profileId: String,
    actionId: String,
    currentFolderId: String,
    resourceType: String,
    initialRequestStatus: String
  };

  static targets = ['announcement', 'detailsButton', 'processedPercentageBar', 'processedPercentageText',
                    'pendingBlock', 'inProgressBlock', 'completedBlock', 'completedWithErrorsBlock', 'failedBlock'];

  initialize() {
    this.statusColors = {
      pending: 'bg-gray-400',
      failed: 'bg-red-400',
      in_progress: 'bg-blue-400',
      completed_with_errors: 'bg-yellow-400',
      completed: 'bg-green-400'
    };
  }

  connect() {
    if (['in_progress', 'pending'].includes(this.initialRequestStatusValue)) {
      this.checkRequestStatus();
      this.statusPollingInterval = setInterval(() => this.checkRequestStatus(), 5000);
    }
    else {
      this.acknowledgeAction();
    }
  }

  disconnect() {
    clearInterval(this.statusPollingInterval);
    clearInterval(this.percentageInterval);
  }

  async checkRequestStatus() {
    if (!this.hasProfileIdValue || !this.hasActionIdValue || !this.hasResourceTypeValue) return;

    try {
      const response = await fetch(
        `/profiles/${this.profileIdValue}/bulk_action_requests/${this.actionIdValue}/status?resource_type=${this.resourceTypeValue}`,
        { method: 'GET', headers: { 'Accept': 'application/json' } }
      );

      const data = await response.json();
      if (data) this.handleActionStatus(data);
    }
    catch (error) {
      console.error('Bulk action request status polling failed: ', error);
      clearInterval(this.statusPollingInterval);
    }
  }

  handleActionStatus(action) {
    this.updateProgressUI(action);

    switch(action.status) {
      case 'pending':
        this.showStatusInfoBlock(this.pendingBlockTarget);
        break;
      case 'in_progress':
        this.showStatusInfoBlock(this.inProgressBlockTarget);
        break;
      case 'completed_with_errors':
        this.finishAction(this.completedWithErrorsBlockTarget);
        break;
      case 'completed':
        this.finishAction(this.completedBlockTarget);
        break;
      case 'failed':
        this.finishAction(this.failedBlockTarget);
        break;
    }
  }

  finishAction(blockToShow) {
    clearInterval(this.statusPollingInterval);
    this.showStatusInfoBlock(blockToShow);
    this.detailsButtonTarget?.classList.remove('hidden');
    this.refreshItemList();
    this.acknowledgeAction();
  }

  showStatusInfoBlock(targetToShow) {
    if (!this.hasAnnouncementTarget) return;

    this.constructor.targets.forEach(name => {
      if (name.endsWith('Block')) {
        const target = this[`${name}Target`];
        target?.classList.add('hidden');
      }
    });

    targetToShow?.classList.remove('hidden');
  }

  updateProgressUI(action) {
    if (!this.hasAnnouncementTarget) return;

    const oldValue = parseInt(this.processedPercentageTextTarget?.innerText || '0');
    const newValue = parseInt(action.processed_items_percentage);

    this.updatePercentage(oldValue, newValue);
    this.processedPercentageBarTarget.style.width = `${newValue}%`;
    this.processedPercentageBarTarget.classList.remove(...Object.values(this.statusColors));
    this.processedPercentageBarTarget.classList.add(this.colorFor(action.status));
  }

  updatePercentage(from, to) {
    if (this.percentageInterval) clearInterval(this.percentageInterval);

    from = Math.max(0, Math.min(100, from));
    to = Math.max(0, Math.min(100, to));

    if (from === to) return;

    const duration = 1500;
    const steps = Math.abs(to - from);
    const stepTime = duration / steps;

    let current = from;
    const increment = to > from ? 1 : -1;

    this.percentageInterval = setInterval(() => {
      current += increment;
      current = Math.max(0, Math.min(100, current));

      this.processedPercentageTextTarget.innerText = `${current}%`;

      if (current === to) clearInterval(this.percentageInterval);
    }, stepTime);
  }

  closeAnnouncement() {
    clearInterval(this.ackTimeoutInterval);
    this.announcementTarget.remove();
  }

  acknowledgeAction() {
    clearInterval(this.statusPollingInterval);

    fetch(`/profiles/${this.profileIdValue}/bulk_action_requests/${this.actionIdValue}`, {
      method: 'PATCH',
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ bulk_action_request: { acknowledged: true } })
    });
  }

  _refreshItemList() {
    visit(`/profiles/${this.profileIdValue}/${this.resourceTypeValue}?folder_id=${this.currentFolderIdValue}`, { frame: this.resourceTypeValue });
  }

  refreshItemList() {
    const url = window.location.pathname + window.location.search

    visit(url.toString(), { frame: this.resourceTypeValue });
  }


  colorFor(status) {
    return this.statusColors[status] || '';
  }
}
